import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, FormProvider, Preloader } from '@common-fe/common-fe';
import _ from 'lodash';

import ROUTES from '@/common/routes';
import { useRedirectToHome } from '@/modules/core/hooks';
import { Footer, Header, Sidebar } from '@/modules/ExpressSetup/ExpressSetupSignUp/components';
import {
  ExpressSetupSignUpErrorText,
  ExpressSetupSignUpServerErrorText,
} from '@/modules/ExpressSetup/ExpressSetupSignUp/expressSetupSignUp.constants';
import useCreateMainContactForm, {
  CreateMainContactField,
  CreateMainContactForm,
} from '@/modules/ExpressSetup/ExpressSetupSignUp/hooks/useCreateMainContactForm';
import useCreateExpressSetupContactQuery from '@/modules/ExpressSetup/ExpressSetupSignUp/queries/useCreateExpressSetupContact.query';
import useGetExpressSetupPropertiesQuery from '@/modules/ExpressSetup/ExpressSetupSignUp/queries/useGetExpressSetupProperties.query';
import { SignInNotification } from '@/modules/user/components/SignIn/hooks/useSignInNotifications';
import { useAuthStore } from '@/modules/user/stores';
import { useQueryParams } from '@/utils/hooks';

import ConsentToPartnership from './ConsentToPartnership/ConsentToPartnership';
import MainContactAccess from './MainContactAccess/MainContactAccess';

const DISTRIBUTOR_ID_QUERY_KEY = 'distributor_id';

const CreateMainContact = () => {
  const query = useQueryParams();
  const { push } = useHistory();
  const { auth } = useAuthStore();
  const { methods } = useCreateMainContactForm();
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const errors = methods.formState.errors;
  const distributorId = useMemo(() => query.get(DISTRIBUTOR_ID_QUERY_KEY) || '', [query]);
  const { expressSetupProperties, isLoading } = useGetExpressSetupPropertiesQuery({ distributorId });
  const { createExpressSetupContact, isLoading: isCreateContactLoading } =
    useCreateExpressSetupContactQuery();

  useRedirectToHome(!isLoading && !expressSetupProperties?.enabled);

  const handleSuccess = useCallback(() => {
    if (auth) {
      push(ROUTES.HOME);
      return;
    }

    push({
      pathname: ROUTES.LOGIN,
      hash: SignInNotification.expressSetupSuccess,
    });
  }, [push, auth]);

  const handleSubmit = useCallback(
    async (formValues: CreateMainContactForm) => {
      setErrorText(undefined);
      try {
        await createExpressSetupContact({
          email: formValues[CreateMainContactField.personalEmail],
          username: formValues[CreateMainContactField.username],
          firstName: formValues[CreateMainContactField.firstName],
          lastName: formValues[CreateMainContactField.lastName],
          password: formValues[CreateMainContactField.password],
          phone: formValues[CreateMainContactField.phoneNumber],
          distributorId,
        });
        handleSuccess();
      } catch (error) {
        const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;

        if (errorMessage.includes(ExpressSetupSignUpServerErrorText.CONTACT_ALREADY_EXISTS)) {
          setErrorText(ExpressSetupSignUpErrorText.CONTACT_ALREADY_EXISTS);
          methods.setError(CreateMainContactField.username, {
            type: 'manual',
            message: ExpressSetupSignUpErrorText.CONTACT_ALREADY_EXISTS,
          });
          return;
        }
      }
    },
    [createExpressSetupContact, methods, distributorId, handleSuccess]
  );

  const isPricingConsentAgreedError = errors?.[CreateMainContactField.isPricingConsentAgreed]?.message;
  const isPrivacyPolicyAgreedError = errors?.[CreateMainContactField.isPrivacyPolicyAgreed]?.message;

  const currentErrorText = useMemo(() => {
    if (isPricingConsentAgreedError || isPrivacyPolicyAgreedError) {
      return ExpressSetupSignUpErrorText.TERMS_AND_CONDITIONS_NOT_CONFIRMED;
    }

    return errorText;
  }, [isPricingConsentAgreedError, isPrivacyPolicyAgreedError, errorText]);

  if (isLoading) {
    return (
      <Box margin="auto">
        <Preloader testId="create-main-contact_preloader" />
      </Box>
    );
  }

  return (
    <Box data-testId="create-main-contact_wrapper" direction="row">
      <Sidebar
        testId="create-main-contact"
        largeLogoUrl={expressSetupProperties?.logoInfo?.largeLogoUrl}
        darkLargeLogoUrl={expressSetupProperties?.logoInfo?.darkLargeLogoUrl}
      />

      <Box flex overflow="auto">
        <div>
          <FormProvider {...methods}>
            <Box pad="spacing48">
              <Header
                title={`Welcome to ${expressSetupProperties?.partnerName} Health Benefit Accounts`}
                description="Setting up your plans is quick and easy. Lets start with some basic information about your company."
              />
              <MainContactAccess />
              <ConsentToPartnership
                partnerName={expressSetupProperties?.partnerName}
                privacyPolicyLink={expressSetupProperties?.privacyPolicyLink}
                termsAndConditionsLink={expressSetupProperties?.termsAndConditionsLink}
              />
            </Box>
          </FormProvider>
          <Footer
            testId="create-main-contact"
            onSubmit={methods.handleSubmit(handleSubmit)}
            isLoading={isCreateContactLoading}
            errorText={currentErrorText}
          />
        </div>
      </Box>
    </Box>
  );
};

export default CreateMainContact;
